/**
 * metaLeadGen/FilterBar.tsx
 *
 * Isolated filter bar for the Meta Lead Generation tab.
 *
 * All options are driven by the metaLeadGen backend service.
 * No shared dashboard state is touched here.
 *
 * Filters rendered:
 *   Row 1 (entity):    Date Range | Campaign | Ad Set | Ad | [Reset]
 *   Row 2 (dimension): Age | Gender | Device | Platform | Creative Type | Country
 */

import { useState, useRef, useEffect } from "react";
import type { MetaFiltersState } from "./useFilters";
import type { FilterOptions } from "./types";

// ─────────────────────────────────────────────
// MULTI-SELECT DROPDOWN
// ─────────────────────────────────────────────
interface SelectOption {
  value: string;
  label: string;
}

interface MultiSelectProps {
  label: string;
  options: SelectOption[];
  value: string[];
  onChange: (value: string[]) => void;
  minWidth?: number;
}

function MultiSelect({ label, options, value, onChange, minWidth = 120 }: MultiSelectProps) {
  const [open, setOpen] = useState(false);
  const [pending, setPending] = useState<string[]>(value);
  const [search, setSearch] = useState("");
  const ref = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);

  // Sync pending when external value changes (e.g. reset)
  useEffect(() => { setPending(value); }, [value]);

  // Close + discard on outside click
  useEffect(() => {
    function handler(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
        setSearch("");
        setPending(value);
      }
    }
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [value]);

  // Focus search on open
  useEffect(() => {
    if (open) searchRef.current?.focus({ preventScroll: true });
  }, [open]);

  const toggle = (v: string) =>
    setPending(prev => prev.includes(v) ? prev.filter(x => x !== v) : [...prev, v]);

  const apply = () => { onChange(pending); setOpen(false); setSearch(""); };
  const clear = () => setPending([]);

  const filtered = options.filter(o =>
    o.label.toLowerCase().includes(search.toLowerCase())
  );

  const displayText =
    value.length === 0 ? label :
    value.length === 1 ? (() => {
      const lbl = options.find(o => o.value === value[0])?.label ?? value[0];
      return lbl.length > 18 ? lbl.slice(0, 18) + "…" : lbl;
    })() :
    `${value.length} selected`;

  const hasChanges =
    JSON.stringify([...pending].sort()) !== JSON.stringify([...value].sort());

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        style={{ minWidth }}
        className={`flex items-center gap-1.5 h-8 px-2.5 rounded-md border text-xs transition-colors ${
          value.length > 0
            ? "border-[var(--brand-primary)] bg-[var(--brand-primary)]/10 text-[var(--brand-primary)]"
            : "border-input bg-background hover:bg-accent hover:text-accent-foreground text-foreground"
        }`}
      >
        <span className="truncate flex-1 text-left">{displayText}</span>
        <svg
          className={`w-3.5 h-3.5 flex-shrink-0 transition-transform ${open ? "rotate-180" : ""}`}
          fill="none" stroke="currentColor" viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {open && (
        <div className="absolute top-full left-0 mt-1 z-[100] bg-popover border border-border rounded-lg shadow-xl min-w-[240px] max-w-[320px]">
          {/* Search */}
          <div className="p-2 border-b border-border">
            <div className="relative">
              <svg className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
              <input
                ref={searchRef}
                type="text"
                placeholder="Search…"
                value={search}
                onChange={e => setSearch(e.target.value)}
                className="w-full h-7 pl-7 pr-2 text-xs border border-border rounded bg-background focus:outline-none focus:ring-1 focus:ring-ring"
              />
            </div>
          </div>

          {/* Options */}
          <div className="max-h-[220px] overflow-y-auto">
            {filtered.length === 0 ? (
              <div className="px-3 py-2 text-xs text-muted-foreground">No options</div>
            ) : (
              filtered.map(opt => {
                const selected = pending.includes(opt.value);
                return (
                  <label
                    key={opt.value}
                    className={`flex items-center gap-2 px-3 py-2 cursor-pointer text-xs transition-colors ${
                      selected
                        ? "bg-[var(--brand-primary)]/10 text-[var(--brand-primary)]"
                        : "hover:bg-accent"
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={selected}
                      onChange={() => toggle(opt.value)}
                      className="rounded border-border accent-[var(--brand-primary)]"
                    />
                    <span className="truncate">{opt.label}</span>
                  </label>
                );
              })
            )}
          </div>

          {/* Footer */}
          <div className="p-2 border-t border-border flex justify-between items-center">
            <span className="text-[10px] text-muted-foreground">
              {pending.length} selected
              {hasChanges && <span className="text-[var(--brand-primary)]"> *</span>}
            </span>
            <div className="flex items-center gap-2">
              {pending.length > 0 && (
                <button type="button" onClick={clear} className="text-xs text-muted-foreground hover:text-foreground">
                  Clear
                </button>
              )}
              <button
                type="button"
                onClick={apply}
                className="px-2.5 py-1 text-xs bg-[var(--brand-primary)] text-white rounded hover:opacity-90 transition-opacity"
              >
                Apply
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────
// FILTER BAR PROPS
// ─────────────────────────────────────────────
interface FilterBarProps {
  state: MetaFiltersState;
  options: FilterOptions;
  adsetOptions: FilterOptions["adsets"];
  adOptions: FilterOptions["ads"];
  onCampaignChange: (ids: string[]) => void;
  onAdsetChange: (ids: string[]) => void;
  onAdChange: (ids: string[]) => void;
  onAgeChange: (v: string[]) => void;
  onGenderChange: (v: string[]) => void;
  onDeviceChange: (v: string[]) => void;
  onPlatformChange: (v: string[]) => void;
  onCreativeTypeChange: (v: string[]) => void;
  onCountryChange: (v: string[]) => void;
  onReset: () => void;
  hasActiveFilters: boolean;
}

// ─────────────────────────────────────────────
// FILTER BAR COMPONENT
// ─────────────────────────────────────────────
export function MetaLeadGenFilterBar({
  state,
  options,
  adsetOptions,
  adOptions,
  onCampaignChange,
  onAdsetChange,
  onAdChange,
  onAgeChange,
  onGenderChange,
  onDeviceChange,
  onPlatformChange,
  onCreativeTypeChange,
  onCountryChange,
  onReset,
  hasActiveFilters,
}: FilterBarProps) {
  const campaignSelectOptions = options.campaigns.map(c => ({ value: c.id, label: c.name }));
  const adsetSelectOptions = adsetOptions.map(a => ({ value: a.id, label: a.name }));
  const adSelectOptions = adOptions.map(a => ({ value: a.id, label: a.name }));
  const ageSelectOptions = options.ages.map(v => ({ value: v, label: v }));
  const genderSelectOptions = options.genders.map(v => ({ value: v, label: v.charAt(0).toUpperCase() + v.slice(1) }));
  const deviceSelectOptions = options.devices.map(v => ({ value: v, label: v.charAt(0).toUpperCase() + v.slice(1) }));
  const platformSelectOptions = options.platforms.map(v => ({
    value: v,
    label: v === "audience_network" ? "Audience Network" : v.charAt(0).toUpperCase() + v.slice(1),
  }));
  const creativeTypeSelectOptions = options.creativeTypes.map(v => ({ value: v, label: v }));
  const countrySelectOptions = options.countries.map(v => ({ value: v, label: v }));

  return (
    <div className="space-y-2">
      {/* Row 1: Entity filters */}
      <div className="flex flex-wrap items-center gap-2">
        {/* Campaign → Ad Set → Ad (cascading) */}
        <MultiSelect
          label="Campaign"
          options={campaignSelectOptions}
          value={state.campaignIds}
          onChange={onCampaignChange}
          minWidth={130}
        />
        <MultiSelect
          label="Ad Set"
          options={adsetSelectOptions}
          value={state.adsetIds}
          onChange={onAdsetChange}
          minWidth={110}
        />
        <MultiSelect
          label="Ad"
          options={adSelectOptions}
          value={state.adIds}
          onChange={onAdChange}
          minWidth={90}
        />

        {/* Reset button — only visible when filters are active */}
        {hasActiveFilters && (
          <>
            <div className="h-4 w-px bg-border" />
            <button
              type="button"
              onClick={onReset}
              className="flex items-center gap-1 h-8 px-2.5 rounded-md border border-dashed border-border text-xs text-muted-foreground hover:border-foreground/40 hover:text-foreground transition-colors"
            >
              <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
              Reset
            </button>
          </>
        )}
      </div>

      {/* Row 2: Dimension filters */}
      <div className="flex flex-wrap items-center gap-2">
        <MultiSelect
          label="Age"
          options={ageSelectOptions}
          value={state.ages}
          onChange={onAgeChange}
          minWidth={90}
        />
        <MultiSelect
          label="Gender"
          options={genderSelectOptions}
          value={state.genders}
          onChange={onGenderChange}
          minWidth={100}
        />
        <MultiSelect
          label="Device"
          options={deviceSelectOptions}
          value={state.devices}
          onChange={onDeviceChange}
          minWidth={100}
        />
        <MultiSelect
          label="Platform"
          options={platformSelectOptions}
          value={state.platforms}
          onChange={onPlatformChange}
          minWidth={110}
        />
        <MultiSelect
          label="Creative Type"
          options={creativeTypeSelectOptions}
          value={state.creativeTypes}
          onChange={onCreativeTypeChange}
          minWidth={130}
        />
        <MultiSelect
          label="Country"
          options={countrySelectOptions}
          value={state.countries}
          onChange={onCountryChange}
          minWidth={100}
        />
      </div>
    </div>
  );
}
